import { Injectable } from '@angular/core';
import { OperationError } from './operation-error';
import { Observable } from 'rxjs';
import { shareReplay, take, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageBusService } from '../message-bus/message-bus.service';
import { MessageBusPayload, MessageKey } from '../message-bus/message-bus.model';

@Injectable({
  providedIn: 'root'
})
export class OperationsTrackerService {
  constructor(private messageBus: MessageBusService) {}

  trackError(operationId: MessageKey, error: Error, partition = ''): void {
    this.messageBus.sendMessage(this.buildKey(operationId, partition), error);
  }

  trackErrorWithData(operationId: MessageKey, error: Error, data: MessageBusPayload, partition = ''): void {
    this.messageBus.sendMessage(this.buildKey(operationId, partition), { error, data });
  }

  trackSuccess(operationId: MessageKey, partition = ''): void {
    this.messageBus.sendMessage(this.buildKey(operationId, partition), null);
  }

  trackSuccessWithData(operationId: MessageKey, data: MessageBusPayload, partition = ''): void {
    this.messageBus.sendMessage(this.buildKey(operationId, partition), data);
  }

  getOperationStatus<TMessage>(operationId: MessageKey, partition = ''): Observable<TMessage> {
    return this.messageBus
      .getObservable<TMessage>(this.buildKey(operationId, partition))
      .pipe(shareReplay({ bufferSize: 1, refCount: true }), take(1));
  }

  getOperationData<TData>(operationId: MessageKey, partition = ''): Observable<TData> {
    return this.getOperationEvents<TData>(operationId, partition).pipe(
      take(1),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getOperationEvents<TData>(operationId: MessageKey, partition = ''): Observable<TData> {
    return this.messageBus.getObservable(this.buildKey(operationId, partition)).pipe(
      tap((data) => {
        if (data instanceof HttpErrorResponse) {
          throw new OperationError(operationId.toString(), data.error, data.status);
        } else if (data instanceof Error) {
          throw new OperationError(operationId.toString(), null, 500);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    ) as Observable<TData>;
  }

  private buildKey(operationId: MessageKey, partition: string): string {
    if (partition) {
      return `${partition}-${operationId}`;
    }

    return operationId.toString();
  }
}
